import { api } from "@convex/_generated/api";
/** Typed Convex function references — uses generated api/internal refs. */
import type { Doc, Id } from "@convex/_generated/dataModel";
import type { FunctionReturnType } from "convex/server";

// ── ai/queries ────────────────────────────────────────────────────────────────

export const aiQueriesListThreads = api.ai.queries.listThreads;

// ── ai/threads ────────────────────────────────────────────────────────────────

export const aiThreadsUpdateTitle = api.ai.threads.updateTitle;

export const aiThreadsDeleteThread = api.ai.threads.deleteThread;

// ── ai/messages ───────────────────────────────────────────────────────────────

export const aiMessagesRetryMessage = api.ai.messages.retryMessage;

export const aiMessagesClearThread = api.ai.messages.clearThread;

export const aiMessagesArchiveThread = api.ai.messages.archiveThread;

export const aiMessagesRestoreThread = api.ai.messages.restoreThread;

export const aiMessagesGetThreadCapabilities =
  api.ai.messages.getThreadCapabilities;

// ── ai/mutations/pendingActions ───────────────────────────────────────────────

export const aiPendingActionsConfirm = api.ai.mutations.pendingActions.confirm;

export const aiPendingActionsReject = api.ai.mutations.pendingActions.reject;

// ── ai/files ──────────────────────────────────────────────────────────────────

export const aiFilesGenerateUploadUrl = api.ai.files.generateUploadUrl;

export const aiFilesSaveUploadedFile = api.ai.files.saveUploadedFile;

export const aiFilesDeleteFile = api.ai.files.deleteFile;

export const aiFilesSendMessageWithFile = api.ai.files.sendMessageWithFile;

export const aiFilesGetFilesForThread = api.ai.files.getFilesForThread;

// ── ai/search ─────────────────────────────────────────────────────────────────

export const aiSearchAcrossThreads = api.ai.search.searchAcrossThreads;

export const aiSearchWithinThread = api.ai.search.searchWithinThread;

// ── billing ───────────────────────────────────────────────────────────────────

export const billingGetTierInfo = api.billing.getTierInfo;

export const billingGetCurrentBillingInfo = api.billing.getCurrentBillingInfo;

export const billingUpgradeToGrowth = api.billing.upgradeToGrowth;

export const billingUpgradeToEnterprise = api.billing.upgradeToEnterprise;

export const billingDowngradeToFree = api.billing.downgradeToFree;

// ── organizations ────────────────────────────────────────────────────────────

export const organizationsGetBusinessInformation =
  api.organizations.getBusinessInformation;

// ── files ────────────────────────────────────────────────────────────────────

export const filesGenerateUploadUrl = api.files.generateUploadUrl;

// ── menus ────────────────────────────────────────────────────────────────────

export const menusCreate = api.menus.create;

export const menusDuplicate = api.menus.duplicate;

export const menusGetAllMine = api.menus.getAllMine;

// ── employees ────────────────────────────────────────────────────────────────

export const employeesGetAll = api.employees.getAll;

export const employeesGetEmployeeByUserId = api.employees.getEmployeeByUserId;

// ── cashDrawer ────────────────────────────────────────────────────────────────

export interface CashDrawerSummary extends Doc<"cashDrawer"> {
  employee: {
    _id: Id<"employees">;
    firstName: string;
    lastName: string;
    imageUrl?: string;
  } | null;
  session: {
    _id: Id<"posDeviceSessions">;
    sessionId: string;
    deviceId: string;
    deviceInfo: {
      model: string;
      os: string;
      appVersion: string;
    };
  } | null;
}

export const cashDrawerGetTransactionsByLocation =
  api.cashDrawer.getTransactionsByLocation;

// ── hardwareOrders ────────────────────────────────────────────────────────────

export const hardwareOrdersListOrders = api.hardwareOrders.listOrders;

export const hardwareOrdersGetAvailableSKUs =
  api.hardwareOrders.getAvailableSKUs;

export const hardwareOrdersGetShippingMethods =
  api.hardwareOrders.getShippingMethods;

export const hardwareOrdersPreviewOrder = api.hardwareOrders.previewOrder;

export const hardwareOrdersCreateOrder = api.hardwareOrders.createOrder;

// ── notifications ─────────────────────────────────────────────────────────────

export const notificationsGetNotifications = api.notifications.getNotifications;

export const notificationsGetUnreadCount = api.notifications.getUnreadCount;

export const notificationsMarkAsRead = api.notifications.markAsRead;

export const notificationsMarkAllAsRead = api.notifications.markAllAsRead;

export const notificationsDeleteNotification =
  api.notifications.deleteNotification;

// ── posTerminalLocations ─────────────────────────────────────────────────────

export const posTerminalLocationsListTerminalLocations =
  api.posTerminalLocations.listTerminalLocations;

export const posTerminalLocationsSetLocationTerminalLocation =
  api.posTerminalLocations.setLocationTerminalLocation;

// ── printer ───────────────────────────────────────────────────────────────────

export const printerGetAllByLocationId = api.printer.getAllByLocationId;

// ── locationDiscounts ───────────────────────────────────────────────────────

export const locationDiscountsGetAllByLocationId =
  api.locationDiscounts.getAllByLocationId;

export type DiscountListResult = FunctionReturnType<
  typeof locationDiscountsGetAllByLocationId
>;
export type DiscountItem = DiscountListResult["discounts"][number];
export type DiscountAvailabilityTime = DiscountItem["availableTimes"][number];

export const locationDiscountsCreate = api.locationDiscounts.create;

// ── locations ─────────────────────────────────────────────────────────────────

export interface LocationListItem {
  id: Id<"locations">;
  name: string;
  phoneNumber?: string;
  logoUrl?: string;
  style: "QSR" | "FSR";
  type: string;
  addressLine1?: string;
  addressLine2?: string | null;
  city?: string;
  state?: string;
  zipCode?: string;
  operationalStatus: string;
  createdAt: number;
  sortOrder: number;
  posStatus: "inactive" | "fully-online" | "partially-online" | "offline";
  taxPercentage?: number;
  taxLastUpdated?: number;
  taxUpdateSource?: string;
  [key: string]: unknown;
}

export const locationsCreate = api.locations.create;

export const locationsUpdateDiscountsSettings =
  api.locations.updateDiscountsSettings;

export const locationsGetAllMine = api.locations.getAllMine;

export const locationsGetById = api.locations.getById;

export type LocationById = FunctionReturnType<typeof locationsGetById>;

export const locationsUpdate = api.locations.update;

export const locationsGetAllMineWithMenus = api.locations.getAllMineWithMenus;

export const locationsUpdateEndOfDayConfig = api.locations.updateEndOfDayConfig;

export const locationsUpdateVoidAndRefundSettings =
  api.locations.updateVoidAndRefundSettings;

export const locationsGetAllMineWithTerminals =
  api.locations.getAllMineWithTerminals;

// ── locationSales ───────────────────────────────────────────────────────────

// ── Report return types ──────────────────────────────────────────────────────

export interface TenderTypeEntry {
  location: { name: string; id: string };
  employee: {
    id: string;
    firstName: string;
    lastName: string;
    imageUrl?: string;
  } | null;
  orderCreatedAt: Date;
  total: number;
  orderId: number;
  billId: string;
  tenderTypes: ("cash" | "debit" | "credit")[];
  tipAmount: number;
  posDevice: { id: string; name: string } | null;
}

export interface ProductMixEntry {
  location: { id: Id<"locations">; name: string };
  posDevice: { id: Id<"posDevices"> | string; name: string } | null;
  menu: { id: Id<"menus">; name: string };
  category: { id: Id<"categories">; name: string };
  item: { id: Id<"items">; name: string };
  salesCategory: { id: Id<"salesCategories">; name: string };
  amountSold: number;
  amountRefunded: number;
  itemsSold: number;
  orderType: string;
  shareOfMenuSales: number;
  shareOfCategorySales: number;
  shareOfItemSales: number;
}

export interface OrderTypeEntry {
  type: string;
  location: { id: Id<"locations">; name: string };
  itemsSold: number;
  amountSold: number;
  shareOfTotalSales: number;
}

export interface VoidedItemEntry {
  location: { name: string; id: Id<"locations"> };
  employee: {
    id: Id<"employees">;
    firstName: string;
    lastName: string;
    imageUrl: string | undefined;
  } | null;
  posDevice: { id: Id<"posDevices"> | string; name: string } | null;
  orderCreatedAt: number;
  voidReason: string;
  orderId: string;
  amountVoided: number;
}

export interface RefundEntry {
  location: { name: string; id: string };
  employee: {
    id: string;
    firstName: string;
    lastName: string;
    imageUrl?: string;
  } | null;
  orderCreatedAt: Date;
  refundMethod: string;
  refundAmount: number;
  oldTotal: number;
  newTotal: number;
  orderId: number;
  billId: string;
}

export interface TaxCollectedEntry {
  taxCategory: { id: string; name: string; value: number; taxCode: string };
  amountSold: number;
  itemsSold: number;
  taxCollected: number;
  location: { id: Id<"locations">; name: string };
}

export interface ProjectionEntry {
  id: string;
  locationId: Id<"locations">;
  locationName: string;
  projectionDate: number;
  timeFrame: string;
  calculatedAt: number;
  projectedSales: number;
  projectedOrders: number;
  projectedAverageOrder: number;
  orderTypeBreakdown: Record<string, { count: number; amount: number }>;
  lastUpdated: number;
}

export interface EmployeeTipEntry {
  location: { id: Id<"locations">; name: string };
  date: number;
  amountSold: number;
  creditTips: number;
  debitTips: number;
  cashTips: number;
  totalTips: number;
}

export interface SalesByEmployeeEntry {
  location: { id: Id<"locations">; name: string };
  employee: {
    id: Id<"employees">;
    firstName: string;
    lastName: string;
    imageUrl?: string;
  };
  billsClosed: number;
  hoursWorked: number;
  amountSold: number;
  shareOfTotalSales: number;
}

export interface SalesTotalsResult {
  grossSales: number;
  netSales: number;
  ordersCount: number;
}

export const locationSalesGetSalesTotals = api.locationSales.getSalesTotals;

// ── locationSales refs ─────────────────────────────────────────────────────

export const locationSalesGetOrdersByTenderType =
  api.locationSales.getOrdersByTenderType;

export const locationSalesGetSalesProductMix =
  api.locationSales.getSalesProductMix;

export const locationSalesGetOrderSalesReportData =
  api.locationSales.getOrderSalesReportData;

export const locationSalesGetOrderTypeReportData =
  api.locationSales.getOrderTypeReportData;

export const locationSalesGetVoidedItemsData =
  api.locationSales.getVoidedItemsData;

export const locationSalesGetRefundData = api.locationSales.getRefundData;

export const locationSalesGetTaxCollected = api.locationSales.getTaxCollected;

export const locationSalesGetProjections = api.locationSales.getProjections;

export const locationSalesGetEmployeeTips = api.locationSales.getEmployeeTips;

export interface EmployeeEarningsByWeekResult {
  employeePayRate: number | undefined;
  days: Record<
    number,
    {
      dayOfTheWeek: number;
      hoursWorked: number;
      clockIn: string;
      clockOut: string;
      cardTips: number;
      cashTips: number;
    }
  >;
}

export const locationSalesGetEmployeeEarningsByWeek =
  api.locationSales.getEmployeeEarningsByWeek;

export const locationSalesGetSalesByEmployee =
  api.locationSales.getSalesByEmployee;

export interface SalesByLocationEntry {
  location: { id: Id<"locations">; name: string };
  grossSales: number;
  netSales: number;
}

export const locationSalesGetSalesByLocation =
  api.locationSales.getSalesByLocation;

export interface SalesByItemEntry {
  location: { id: Id<"locations">; name: string };
  itemName: string;
  amountSold: number;
  itemsSold: number;
  orderType: string;
  shareOfTotalSales: number;
}

export const locationSalesGetSalesByItem = api.locationSales.getSalesByItem;

export interface SalesByMenuEntry {
  location: { id: Id<"locations">; name: string };
  menuName: string;
  amountSold: number;
  itemsSold: number;
  orderType: string;
  shareOfTotalSales: number;
}

export const locationSalesGetSalesByMenu = api.locationSales.getSalesByMenu;

export interface SaleByCategoryEntry {
  location: { id: Id<"locations">; name: string };
  categoryName: string;
  amountSold: number;
  itemsSold: number;
  orderType: string;
  shareOfTotalSales: number;
}

export const locationSalesGetSalesByCategory =
  api.locationSales.getSalesByCategory;

export interface SalesBySalesCategoryEntry {
  salesCategory: { id: string; name: string };
  ordersCount: number;
  salesTotal: number;
}

export const locationSalesGetSalesBySalesCategory =
  api.locationSales.getSalesBySalesCategory;

// ── merchantSalesAnalytics ──────────────────────────────────────────────────

export interface MerchantProjectionEntry {
  id: string;
  locationId: string;
  locationName: string;
  projectionDate: number;
  timeFrame: string;
  calculatedAt: number;
  projectedSales: string;
  projectedOrders: string;
  projectedAverageOrder: string;
  orderTypeBreakdown: Record<string, { count: number; amount: number }>;
  lastUpdated: number;
}

export const merchantSalesAnalyticsGetProjections =
  api.merchantSalesAnalytics.getProjections;

// ── taxCategories ────────────────────────────────────────────────────────────

export const taxCategoriesGetAll = api.taxCategories.getAll;

// ── stripeTax (tax category sync) ───────────────────────────────────────────

export const stripeTaxManualSyncTaxCategoryRates =
  api.stripeTax.manualSyncTaxCategoryRates;

// ── salesCategories ───────────────────────────────────────────────────────────

export const salesCategoriesGetAll = api.salesCategories.getAll;

// ── stripeAccounts ────────────────────────────────────────────────────────────

export const stripeAccountsCreateAccount = api.stripeAccounts.createAccount;

export const stripeAccountsCreateAccountSession =
  api.stripeAccounts.createAccountSession;

export const stripeAccountsCheckOnboardingStatus =
  api.stripeAccounts.checkOnboardingStatus;

// ── stripeSetupIntents ────────────────────────────────────────────────────────

export const stripeSetupIntentsCreateSetupIntent =
  api.stripeSetupIntents.createSetupIntent;

export const stripeSetupIntentsGetDefaultPaymentMethod =
  api.stripeSetupIntents.getDefaultPaymentMethod;

// ── stripeTax ─────────────────────────────────────────────────────────────────

export const stripeTaxGetStaleLocations = api.stripeTax.getStaleLocations;

export const stripeTaxManualSyncTaxRate = api.stripeTax.manualSyncTaxRate;

export const stripeTaxManuallyUpdateTaxRate =
  api.stripeTax.manuallyUpdateTaxRate;

// ── stripeTerminal ────────────────────────────────────────────────────────────

export const stripeTerminalListLocations = api.stripeTerminal.listLocations;

export const stripeTerminalListReaders = api.stripeTerminal.listReaders;

export const stripeTerminalDeleteReader = api.stripeTerminal.deleteReader;

export const stripeTerminalCreateLocation = api.stripeTerminal.createLocation;

export const stripeTerminalRegisterReader = api.stripeTerminal.registerReader;

export const stripeTerminalUpdateReader = api.stripeTerminal.updateReader;

// ── menus (additional) ──────────────────────────────────────────────────────

export const menusGetById = api.menus.getById;

export const menusGetMenuDetails = api.menus.getMenuDetails;

export const menusDeleteMenu = api.menus.deleteMenu;

export const menusUpdate = api.menus.update;

export const menusSearchElements = api.menus.searchElements;

// ── categories ──────────────────────────────────────────────────────────────

export interface CategoryListItem {
  id: string;
  _id?: string;
  categoryId: string;
  name: string;
  color: string;
  matchBusinessHours: boolean;
  isCategoryLockEnabled: boolean;
  isShared: boolean;
  itemCount: number | null;
  sortOrder?: number;
  menuId?: string;
  availabilityTimes:
    | {
        dayOfTheWeek: string;
        startTime: string;
        endTime: string;
        [key: string]: unknown;
      }[]
    | null;
  menuRelations: {
    menu: { id: string; _id: string; name: string; [key: string]: unknown };
    [key: string]: unknown;
  }[];
  [key: string]: unknown;
}

export const categoriesGetForMenu = api.categories.getForMenu;

export const categoriesGetById = api.categories.getById;

export interface CategoryPaginatedItem {
  id: string;
  _id: string;
  name: string;
  color: string;
  itemCount: number;
  menuRelations: {
    menu: { id: string; _id: string; name: string; [key: string]: unknown };
    [key: string]: unknown;
  }[];
  [key: string]: unknown;
}

export const categoriesGetAll = api.categories.getAll;

export const categoriesUpdate = api.categories.update;

export const categoriesCloneAndUpdate = api.categories.cloneAndUpdate;

// ── items ───────────────────────────────────────────────────────────────────

export interface ItemListItem {
  id: string;
  _id?: string;
  name: string;
  shortName?: string;
  description?: string;
  price: number;
  imageUrl?: string | null;
  isOutOfStock: boolean;
  isShared?: boolean;
  taxCategoryId?: string | null;
  salesCategoryId?: string | null;
}

export interface ItemWithRelations {
  _id: Id<"items">;
  name: string;
  categoryRelations: Array<{
    category: {
      _id: string;
      id?: string;
      name: string;
      menuRelations?: Array<{
        menu: { _id: string; id?: string; name: string };
      }>;
    };
  }>;
  modifierGroupsCount?: string;
}

export const itemsGetForCategory = api.items.getForCategory;

export const itemsGetAllFromMenu = api.items.getAllFromMenu;

export const itemsGetOne = api.items.getOne;

export const itemsGetAll = api.items.getAll;

export const itemsCreate = api.items.create;

export const itemsRemoveFromCategory = api.items.removeFromCategory;

export const itemsRemove = api.items.remove;

export const itemsAssignToCategories = api.items.assignToCategories;

// ── modifiers ───────────────────────────────────────────────────────────────

export interface ModifierGroupListItem {
  _id: string;
  name: string;
  numberOfModifiers?: number;
  numberOfSubModifiersGroups?: number;
  isSomeOutOfStock?: boolean;
  isShared?: boolean;
  [key: string]: unknown;
}

export interface ModifierGroupDetail {
  _id: Id<"modifierGroups">;
  name: string;
  minimum: number;
  maximum: number;
  type: string;
  sortOrder: number;
  parentModifierGroupId?: Id<"modifierGroups"> | string | null;
  modifiers: {
    _id: string;
    name: string;
    additionalCost: number;
    isOutOfStock: boolean;
    canBeMultiplied: boolean;
    sortOrder: number;
    [key: string]: unknown;
  }[];
  subModifierGroups: {
    _id: string;
    name: string;
    minimum: number;
    maximum: number;
    type: string;
    sortOrder: number;
    parentModifierGroupId?: Id<"modifierGroups"> | string | null;
    modifiers: {
      _id: string;
      name: string;
      additionalCost: number;
      isOutOfStock: boolean;
      canBeMultiplied: boolean;
      sortOrder: number;
      [key: string]: unknown;
    }[];
    [key: string]: unknown;
  }[];
  categoryRelations: { categoryId: string; [key: string]: unknown }[];
  itemRelations: { itemId: string; [key: string]: unknown }[];
  [key: string]: unknown;
}

export const modifiersGetAllGroupsForMenu = api.modifiers.getAllGroupsForMenu;

export const modifiersGetAllGroupsForCategory =
  api.modifiers.getAllGroupsForCategory;

export const modifiersGetAllGroupsForCategoryItems =
  api.modifiers.getAllGroupsForCategoryItems;

export const modifiersGetAllGroups = api.modifiers.getAllGroups;

export const modifiersGetModifierGroup = api.modifiers.getModifierGroup;

export const modifiersCreateGroup = api.modifiers.createGroup;

export const modifiersDeleteById = api.modifiers.deleteById;

export const modifiersUpdateModifierGroup = api.modifiers.updateModifierGroup;

export const modifiersApplyModifierGroup = api.modifiers.applyModifierGroup;

// ── posDevices ──────────────────────────────────────────────────────────────

export const posDevicesGetDevicesByLocationId =
  api.posDevices.getDevicesByLocationId;

export type DevicesListResult = FunctionReturnType<
  typeof posDevicesGetDevicesByLocationId
>;

export const posDevicesGetDeviceById = api.posDevices.getDeviceById;

export const posDevicesUpdate = api.posDevices.update;

export const posDevicesDeleteDevice = api.posDevices.deleteDevice;

// ── posDevicePaymentTerminal ────────────────────────────────────────────────

export const posDevicePaymentTerminalCreate =
  api.posDevicePaymentTerminal.create;

export const posDevicePaymentTerminalUpdate =
  api.posDevicePaymentTerminal.update;

export const posDevicePaymentTerminalDeleteTerminal =
  api.posDevicePaymentTerminal.deleteTerminal;

export const posDevicePaymentTerminalDeleteByPosDeviceId =
  api.posDevicePaymentTerminal.deleteByPosDeviceId;

// ── kds ─────────────────────────────────────────────────────────────────────

export interface KdsDeviceWithStation {
  _id: Id<"kdsDevices">;
  _creationTime: number;
  locationId: Id<"locations">;
  deviceId?: string;
  name: string;
  active: boolean;
  stationId?: Id<"stations"> | null;
  stationName: string | null;
  stationColor: string | null;
  hasActiveSession: boolean;
  lastSessionHeartbeat: number | null;
  lastPing?: number;
  forceResetAt?: number | null;
  [key: string]: unknown;
}

export const kdsGetDevicesWithStations = api.kds.getDevicesWithStations;

export const kdsTriggerDeviceReset = api.kds.triggerDeviceReset;

export const kdsGetDevicesByLocation = api.kds.getDevicesByLocation;

// ── locationDiscounts (additional) ────────────────────────────────────────

export const locationDiscountsUpdate = api.locationDiscounts.update;

export const locationDiscountsDeleteDiscount =
  api.locationDiscounts.deleteDiscount;

// ── locationReceiptSettings ───────────────────────────────────────────────

export const locationReceiptSettingsGetCustomerReceiptSettings =
  api.locationReceiptSettings.getLocationCustomerReceiptSettings;

export const locationReceiptSettingsUpdateCustomerReceiptSettings =
  api.locationReceiptSettings.updateLocationCustomerReceiptSettings;

export const locationReceiptSettingsGetCustomerBillSettings =
  api.locationReceiptSettings.getLocationCustomerBillReceiptSettings;

export const locationReceiptSettingsUpdateCustomerBillSettings =
  api.locationReceiptSettings.updateLocationCustomerBillReceiptSettings;

export const locationReceiptSettingsGetKitchenTicketSettings =
  api.locationReceiptSettings.getLocationKitchenTicketReceiptSettings;

export const locationReceiptSettingsUpdateKitchenTicketSettings =
  api.locationReceiptSettings.updateLocationKitchenTicketReceiptSettings;

// ── locationTipsSettings ──────────────────────────────────────────────────

export interface TipsSettings {
  _id: Id<"locationTipsSettings">;
  _creationTime: number;
  locationId: Id<"locations">;
  tipMethod: "in-pos" | "on-receipt" | "disabled";
  suggestedAmounts?: Array<{
    type: "percentage" | "fixed-amount";
    value: number;
  }>;
}

export const locationTipsSettingsGet = api.locationTipsSettings.getTipsSettings;

export const locationTipsSettingsUpdate =
  api.locationTipsSettings.updateTipsSettings;

// ── locationDayReports ────────────────────────────────────────────────────

export const locationDayReportsGetEmployeeReport =
  api.locationDayReports.getEmployeeReport;

export const locationDayReportsGetBusinessReport =
  api.locationDayReports.getBusinessReport;

// ── locationEndofdaySettings ──────────────────────────────────────────────

export const locationEndofdaySettingsGetEmployeeReportSettings =
  api.locationEndofdaySettings.getLocationEmployeeReportSettings;

export const locationEndofdaySettingsUpdateEmployeeReportSettings =
  api.locationEndofdaySettings.updateLocationEmployeeReportSettings;

export const locationEndofdaySettingsGetBusinessReportSettings =
  api.locationEndofdaySettings.getLocationBusinessReportSettings;

export const locationEndofdaySettingsUpdateBusinessReportSettings =
  api.locationEndofdaySettings.updateLocationBusinessReportSettings;

// ── organizations (additional) ──────────────────────────────────────────────

export const organizationsUpdateBusinessInformation =
  api.organizations.updateBusinessInformation;

export const employeesCreate = api.employees.create;

export const employeeShiftsCreate = api.employeeShifts.create;

// ── employees (additional) ──────────────────────────────────────────────────

export const employeesGetById = api.employees.getById;

export const employeesCheckIfPinInUse = api.employees.checkIfPinInUse;

export const employeesUpdateById = api.employees.updateById;

export const employeesDisableById = api.employees.disableById;

export const employeesEnableById = api.employees.enableById;

// ── organizationRoles ───────────────────────────────────────────────────────

export const organizationRolesGetRoles = api.organizationRoles.getRoles;

export const organizationRolesEnsureDefaultRolesExist =
  api.organizationRoles.ensureDefaultRolesExist;

export const organizationRolesDeleteRole = api.organizationRoles.deleteRole;

export const organizationRolesCreateRole = api.organizationRoles.createRole;

export const organizationRolesUpdateRole = api.organizationRoles.updateRole;

export const organizationRolesGetRoleById = api.organizationRoles.getRoleById;

export type OrganizationRole = NonNullable<
  FunctionReturnType<typeof organizationRolesGetRoleById>
>;

export const organizationRolesGetRoleEmployees =
  api.organizationRoles.getRoleEmployees;

// ── salesCategories (additional) ────────────────────────────────────────────

export const salesCategoriesUpdate = api.salesCategories.update;

export const salesCategoriesCreate = api.salesCategories.create;

// ── taxCategories (additional) ──────────────────────────────────────────────

export const taxCategoriesUpdate = api.taxCategories.update;

export const taxCategoriesGetStripeTaxCodes =
  api.taxCategories.getStripeTaxCodes;

export const taxCategoriesCreate = api.taxCategories.create;

// ── stations ────────────────────────────────────────────────────────────────

export interface StationWithDetails {
  _id: Id<"stations">;
  _creationTime: number;
  locationId: Id<"locations">;
  name: string;
  color: string;
  sortOrder: number;
  hasScreen: boolean;
  isDefault: boolean;
  routingMode: "all" | "categories" | "items";
  printerIds?: Id<"printers">[];
  categoryIds: Id<"categories">[];
  itemIds: Id<"items">[];
  pairedDevices: Doc<"kdsDevices">[];
  printers: Array<Doc<"printers"> | null>;
  [key: string]: unknown;
}

export const stationsGetByLocationId = api.stations.getByLocationId;

export const stationsGetById = api.stations.getById;

export const stationsCreate = api.stations.create;

export const stationsUpdate = api.stations.update;

export const stationsDeleteStation = api.stations.deleteStation;

export const stationsPairDevice = api.stations.pairDevice;

export const stationsUnpairDevice = api.stations.unpairDevice;

// ── storefrontAdmin ─────────────────────────────────────────────────────────

export const storefrontAdminGetStorefrontSettings =
  api.storefrontAdmin.getStorefrontSettings;

export const storefrontAdminUpdateStorefrontSettings =
  api.storefrontAdmin.updateStorefrontSettings;

export const storefrontAdminGetDeliveryZones =
  api.storefrontAdmin.getDeliveryZones;

export const storefrontAdminUpdateDeliveryZones =
  api.storefrontAdmin.updateDeliveryZones;

export const storefrontAdminUpdateLocationCoordinates =
  api.storefrontAdmin.updateLocationCoordinates;

// ── files (additional) ──────────────────────────────────────────────────────

export const filesSaveStorageId = api.files.saveStorageId;

export const filesGetFileUrl = api.files.getFileUrl;

// ── organizations (vortex-auth wrappers) ────────────────────────────────────

export const organizationsListMyOrgs = api.organizations.listMyOrgs;

export const organizationsGetActive = api.organizations.getActive;

export const organizationsSetActive = api.organizations.setActive;

export const organizationsCreateOrganization =
  api.organizations.createOrganization;

// ── organizations (member CRUD — F4) ────────────────────────────────────────
// These match the function-reference shape expected by
// `VortexOrganizationMembersSurface`. `membershipId` is the stable JWT subject
// (opaque string at the surface boundary).

export const organizationsListMembers = api.organizations.listMembers;

export const organizationsInviteMember = api.organizations.inviteMember;

export const organizationsSetMemberRole = api.organizations.setMemberRole;

export const organizationsSuspendMember = api.organizations.suspendMember;

export const organizationsReactivateMember = api.organizations.reactivateMember;
